import React from 'react'
import AppFormFooterBar from '../../../components/AppFormFooterBar'
import {
  AcademicCurriculumSection,
  ScheduleTimingSection,
  OrganizationScopeSection,
  TestScoringSection,
} from '../../../components/education-form'
import TestTipsCard from '../components/TestTipsCard'
import useTestEntryScreen from './hooks/useTestEntryScreen'

export default function TestEntryScreen() {
  const { state, actions } = useTestEntryScreen()
  const { form, isEdit, options, isSaving, isLoading } = state

  if (isLoading) {
    return (
      <div className="mx-auto flex w-full max-w-[1460px] items-center justify-center py-24 text-sm font-semibold text-on-surface-variant">
        <div className="flex flex-col items-center gap-2">
          <span className="material-symbols-outlined animate-spin text-3xl text-primary">progress_activity</span>
          <span>Loading test details...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-20">
      {/* ── Header Title & Breadcrumb ── */}
      <section className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-primary">Education Operations</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-on-surface">
            {isEdit ? 'Edit Test' : 'Add New Test'}
          </h1>
        </div>
      </section>

      {/* ── Main 2-Column Grid: 4 Form Cards (8 cols) + Sticky TIPS Panel (4 cols) ── */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 items-start">
        
        {/* ── Left Column: 4 Categorized Modular Cards ── */}
        <div className="lg:col-span-8 flex flex-col gap-6">

          {/* ── Card 1: Academic & Curriculum Scope ── */}
          <AcademicCurriculumSection
            academicYear={form.academicYear}
            onAcademicYearChange={(val) => actions.patchForm('academicYear', val)}
            academicYearOptions={options.academicYears}
            course={form.course}
            onCourseChange={(val) => actions.patchForm('course', val)}
            courseOptions={options.courses}
            subject={form.subject}
            onSubjectChange={(val) => actions.patchForm('subject', val)}
            subjectOptions={options.subjects}
            showTopic={true}
            topic={form.topic}
            onTopicChange={(val) => actions.patchForm('topic', val)}
            topicLabel="Lesson / Topic Name"
            topicPlaceholder="Enter test lesson or topic name..."
          />

          {/* ── Card 2: Schedule & Timing ── */}
          <ScheduleTimingSection
            showDate={true}
            date={form.testDate}
            onDateChange={(val) => actions.patchForm('testDate', val)}
            dateLabel="Test Date"
            showTillDate={false}
            showTime={true}
            planHour={form.planHour}
            planMinute={form.planMinute}
            planAmPm={form.planAmPm}
            onPlanHourChange={(val) => actions.patchForm('planHour', val)}
            onPlanMinuteChange={(val) => actions.patchForm('planMinute', val)}
            onPlanAmPmChange={(val) => actions.patchForm('planAmPm', val)}
            tillHour={form.tillHour}
            tillMinute={form.tillMinute}
            tillAmPm={form.tillAmPm}
            onTillHourChange={(val) => actions.patchForm('tillHour', val)}
            onTillMinuteChange={(val) => actions.patchForm('tillMinute', val)}
            onTillAmPmChange={(val) => actions.patchForm('tillAmPm', val)}
          />

          {/* ── Card 3: Organization & Target Scope ── */}
          <OrganizationScopeSection
            company={form.company}
            onCompanyChange={(val) => actions.patchForm('company', val)}
            companyOptions={options.companies}
            showCompanyScope={true}
            companyScope={form.companyScope}
            onCompanyScopeChange={(val) => actions.patchForm('companyScope', val)}
            branch={form.branch}
            onBranchChange={(val) => actions.patchForm('branch', val)}
            branchOptions={options.branches}
            showBranchScope={true}
            branchScope={form.branchScope}
            onBranchScopeChange={(val) => actions.patchForm('branchScope', val)}
            batch={form.batch}
            onBatchChange={(val) => actions.patchForm('batch', val)}
            batchOptions={options.batches}
            showBatchScope={true}
            batchScope={form.batchScope}
            onBatchScopeChange={(val) => actions.patchForm('batchScope', val)}
            school={form.schoolCollege}
            onSchoolChange={(val) => actions.patchForm('schoolCollege', val)}
            schoolOptions={options.schools}
            showSchoolScope={true}
            schoolScope={form.schoolCollegeScope}
            onSchoolScopeChange={(val) => actions.patchForm('schoolCollegeScope', val)}
          />

          {/* ── Card 4: Examination Scoring & Details ── */}
          <TestScoringSection
            testNo={form.testNo}
            onTestNoChange={(val) => actions.patchForm('testNo', val)}
            showIgnoreDuplication={true}
            ignoreDuplication={form.ignoreDuplication}
            onIgnoreDuplicationChange={(val) => actions.patchForm('ignoreDuplication', val)}
            totalMarks={form.totalMarks}
            onTotalMarksChange={(val) => actions.patchForm('totalMarks', val)}
            minMarks={form.minMarks}
            onMinMarksChange={(val) => actions.patchForm('minMarks', val)}
            department={form.department}
            onDepartmentChange={(val) => actions.patchForm('department', val)}
            departmentOptions={options.departments}
            testGroup={form.testGroup}
            onTestGroupChange={(val) => actions.patchForm('testGroup', val)}
            testGroupOptions={options.groups}
            semester={form.semester}
            onSemesterChange={(val) => actions.patchForm('semester', val)}
            testType={form.testType}
            onTestTypeChange={(val) => actions.patchForm('testType', val)}
            examType={form.examType}
            onExamTypeChange={(val) => actions.patchForm('examType', val)}
            examFor={form.examFor}
            onExamForChange={(val) => actions.patchForm('examFor', val)}
          />

        </div>

        {/* ── Right Column: Sticky TIPS & Guidelines Panel (4 cols) ── */}
        <div className="lg:col-span-4 sticky top-6">
          <TestTipsCard />
        </div>

      </div>

      {/* ── Global Sticky Form Footer Bar ── */}
      <AppFormFooterBar
        cancelPath="/education/tests"
        isSaving={isSaving}
        showSaveAndContinue={!isEdit}
        onSaveContinue={() => actions.handleSave(true)}
        onSaveExit={() => actions.handleSave(false)}
        saveLabel={isEdit ? 'Update Test' : 'Save & Exit'}
      />
    </div>
  )
}
